import 'src/configureZod';

import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import AggregateError from 'es-aggregate-error';
import any from 'promise.any';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import ReactModal from 'react-modal';
import { BrowserRouter } from 'react-router-dom';

import 'client-web-sdk/index.css';

import './index.css';
import { setPublicDataBaseUri } from 'client-core';
import {
  initLocale,
  LangAttributeWrapper,
  LoginEditContextProvider,
  UIModeContextProvider,
  WrapperProvider,
} from 'client-web-sdk';

import AppWrapper from './containers/AppWrapper';
import ErrorBoundary from './containers/ErrorBoundary';
import { messages as messagesDe } from './locales/de/messages.po';
import { messages as messagesEn } from './locales/en/messages.po';
import { BlockNavigationContextProvider } from './util/BlockNavigationContext';
import BrowserOutdatedBannerWrapper from './util/BrowserOutdatedBannerWrapper';
import { SharedClientCoreSubscriptionContextProvider } from './util/clientCore/SharedClientCoreSubscriptionContext';
import { ConfettiContextProvider } from './util/ConfettiContext';
import { ConfirmationModalContextProvider } from './util/ConfirmationModalContext';
import { DisplayErrorContextProvider } from './util/DisplayErrorContext';
import { DragContextProvider } from './util/drag/DragContext';
import { DndContextProvider } from './util/dragdrop/DndContext';
import { ExtensionStateContextProvider } from './util/ExtensionStateContext';
import { getPublicDataUrl } from './util/getBackendUrls';
import initSentry from './util/initSentry';
import { LoginVaultViewDataContextProvider } from './util/LoginVaultViewDataContext';
import { MediaQueryContextProvider, useMediaQueryContext } from './util/MediaQueryContext';
import { MovedLoginContextProvider } from './util/MovedLoginContext';
import PreloadErrorHandler from './util/PreloadErrorHandler';
import { RecentActionIdContextProvider } from './util/RecentActionIdContext';
import { WebDebugVisibleContextProvider } from './util/WebDebugVisibleContextProvider';

// connect to sentry
initSentry();

setPublicDataBaseUri(getPublicDataUrl());

// apply polyfills
AggregateError.shim();
any.shim();

// See http://reactcommunity.org/react-modal/accessibility/#app-element
ReactModal.setAppElement('#root');

function RootAttributesHelper({ children }: React.PropsWithChildren<{}>) {
  // apply classes to the html element according to the column count
  const { columns } = useMediaQueryContext();

  useEffect(() => {
    if (columns === 1) {
      document.documentElement.classList.add('singlecol');
      document.documentElement.classList.remove('multicol');
    } else {
      document.documentElement.classList.remove('singlecol');
      document.documentElement.classList.add('multicol');
    }
  });

  return <>{children}</>;
}

initLocale({ en: messagesEn, de: messagesDe });

// render the app with all its wrappers and providers
const container = document.getElementById('root')!;
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <I18nProvider i18n={i18n}>
      <ErrorBoundary>
        <WrapperProvider
          wrappers={[
            PreloadErrorHandler,
            BrowserRouter,
            UIModeContextProvider,
            WebDebugVisibleContextProvider,
            ErrorBoundary,
            DisplayErrorContextProvider,
            LoginEditContextProvider,
            MediaQueryContextProvider,
            RecentActionIdContextProvider,
            ConfettiContextProvider,
            ConfirmationModalContextProvider,
            RootAttributesHelper,
            LoginVaultViewDataContextProvider,
            SharedClientCoreSubscriptionContextProvider,
            MovedLoginContextProvider,
            BlockNavigationContextProvider,
            DragContextProvider,
            DndContextProvider,
            ExtensionStateContextProvider,
            LangAttributeWrapper,
            BrowserOutdatedBannerWrapper,
          ]}
        >
          <AppWrapper />
        </WrapperProvider>
      </ErrorBoundary>
    </I18nProvider>
  </React.StrictMode>,
);
